import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { createAccount, getUserAccount } from './accountService';

/**
 * Migrate a user to account structure
 * Creates account if needed and updates user document
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<string>} - Account ID
 */
export const migrateUserToAccount = async (userId) => {
  try {
    // Check if user already has an account
    const existingAccount = await getUserAccount(userId);
    if (existingAccount) {
      console.log(`User ${userId} already has account ${existingAccount.id}`);
      return existingAccount.id;
    }

    // Get user document to extract company fields
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();

    // Create account with company fields from user
    const accountId = await createAccount(userId, {
      companyName: userData.companyName || '',
      companyAddress: userData.companyAddress || '',
      companyPhone: userData.companyPhone || '',
      companyEmail: userData.companyEmail || '',
      companyWebsite: userData.companyWebsite || '',
      companyIndustry: userData.companyIndustry || '',
      companySize: userData.companySize || '1-10',
      accountType: userData.accountType || 'free'
    });

    // Update user document with accountId
    await updateDoc(doc(db, 'users', userId), {
      accountId,
      updatedAt: Timestamp.now()
    });

    console.log(`Migrated user ${userId} to account ${accountId}`);
    return accountId;
  } catch (error) {
    console.error('Error migrating user to account:', error);
    throw new Error('Failed to migrate user to account');
  }
};

/**
 * Migrate user's posts to include accountId
 * @param {string} userId - Firebase Auth UID
 * @param {string} accountId - Account ID
 * @returns {Promise<number>} - Number of posts migrated
 */
export const migrateUserPosts = async (userId, accountId) => {
  try {
    const q = query(
      collection(db, 'posts'),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`No posts found for user ${userId}`);
      return 0;
    }

    // Batch update posts
    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnap => {
      const postRef = doc(db, 'posts', docSnap.id);
      batch.update(postRef, {
        accountId,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();
    console.log(`Migrated ${snapshot.docs.length} posts for user ${userId}`);
    return snapshot.docs.length;
  } catch (error) {
    console.error('Error migrating user posts:', error);
    throw new Error('Failed to migrate user posts');
  }
};

/**
 * Migrate user's templates to include accountId
 * @param {string} userId - Firebase Auth UID
 * @param {string} accountId - Account ID
 * @returns {Promise<number>} - Number of templates migrated
 */
export const migrateUserTemplates = async (userId, accountId) => {
  try {
    const q = query(
      collection(db, 'templates'),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`No templates found for user ${userId}`);
      return 0;
    }

    // Batch update templates
    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnap => {
      const templateRef = doc(db, 'templates', docSnap.id);
      batch.update(templateRef, {
        accountId,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();
    console.log(`Migrated ${snapshot.docs.length} templates for user ${userId}`);
    return snapshot.docs.length;
  } catch (error) {
    console.error('Error migrating user templates:', error);
    throw new Error('Failed to migrate user templates');
  }
};

/**
 * Migrate user's scheduled posts to include accountId
 * @param {string} userId - Firebase Auth UID
 * @param {string} accountId - Account ID
 * @returns {Promise<number>} - Number of scheduled posts migrated
 */
export const migrateUserScheduledPosts = async (userId, accountId) => {
  try {
    const q = query(
      collection(db, 'scheduledPosts'),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`No scheduled posts found for user ${userId}`);
      return 0;
    }

    // Batch update scheduled posts
    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnap => {
      const postRef = doc(db, 'scheduledPosts', docSnap.id);
      batch.update(postRef, {
        accountId,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();
    console.log(`Migrated ${snapshot.docs.length} scheduled posts for user ${userId}`);
    return snapshot.docs.length;
  } catch (error) {
    console.error('Error migrating user scheduled posts:', error);
    throw new Error('Failed to migrate user scheduled posts');
  }
};

/**
 * Migrate user's workspaces to include accountId
 * @param {string} userId - Firebase Auth UID
 * @param {string} accountId - Account ID
 * @returns {Promise<number>} - Number of workspaces migrated
 */
export const migrateUserWorkspaces = async (userId, accountId) => {
  try {
    // Workspaces are stored in subcollection: users/{userId}/workspaces
    const workspacesRef = collection(db, 'users', userId, 'workspaces');
    const snapshot = await getDocs(workspacesRef);

    if (snapshot.empty) {
      console.log(`No workspaces found for user ${userId}`);
      return 0;
    }

    // Batch update workspaces
    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnap => {
      const workspaceRef = doc(db, 'users', userId, 'workspaces', docSnap.id);
      batch.update(workspaceRef, {
        accountId,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();
    console.log(`Migrated ${snapshot.docs.length} workspaces for user ${userId}`);
    return snapshot.docs.length;
  } catch (error) {
    console.error('Error migrating user workspaces:', error);
    throw new Error('Failed to migrate user workspaces');
  }
};

/**
 * Migrate user's social connections to account-level
 * @param {string} userId - Firebase Auth UID
 * @param {string} accountId - Account ID
 * @returns {Promise<number>} - Number of connections migrated
 */
export const migrateUserSocialConnections = async (userId, accountId) => {
  try {
    const q = query(
      collection(db, 'socialConnections'),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log(`No social connections found for user ${userId}`);
      return 0;
    }

    // Batch update social connections - keep userId but add accountId
    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnap => {
      const connectionRef = doc(db, 'socialConnections', docSnap.id);
      batch.update(connectionRef, {
        accountId,
        updatedAt: Timestamp.now()
      });
    });

    await batch.commit();
    console.log(`Migrated ${snapshot.docs.length} social connections for user ${userId}`);
    return snapshot.docs.length;
  } catch (error) {
    console.error('Error migrating user social connections:', error);
    throw new Error('Failed to migrate user social connections');
  }
};

/**
 * Complete migration for a user
 * Migrates user and all related data to account structure
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<object>} - Migration summary
 */
export const migrateUserComplete = async (userId) => {
  try {
    console.log(`Starting complete migration for user ${userId}`);

    // Step 1: Migrate user to account (creates account if needed)
    const accountId = await migrateUserToAccount(userId);

    // Step 2: Migrate all user data
    const postsCount = await migrateUserPosts(userId, accountId);
    const templatesCount = await migrateUserTemplates(userId, accountId);
    const scheduledPostsCount = await migrateUserScheduledPosts(userId, accountId);
    const workspacesCount = await migrateUserWorkspaces(userId, accountId);
    const connectionsCount = await migrateUserSocialConnections(userId, accountId);

    const summary = {
      userId,
      accountId,
      migrated: {
        posts: postsCount,
        templates: templatesCount,
        scheduledPosts: scheduledPostsCount,
        workspaces: workspacesCount,
        socialConnections: connectionsCount
      },
      timestamp: new Date().toISOString()
    };

    console.log('Migration complete:', summary);
    return summary;
  } catch (error) {
    console.error('Error during complete migration:', error);
    throw new Error('Failed to complete user migration');
  }
};

/**
 * Check if user needs migration
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<boolean>} - True if migration needed
 */
export const needsMigration = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();

    // User needs migration if they don't have an accountId
    return !userData.accountId;
  } catch (error) {
    console.error('Error checking migration status:', error);
    return false;
  }
};
